import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  FormControl,
  Autocomplete,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
} from '@mui/material';
import AutoCompletePlaces from '../../Components/AutoCompletePlaces';
import MediaUpload from '../../Components/MediaUpload';
import { request } from '../../Utils/request';
import { PostUI } from './PostUI';
import { extractHashtags } from '../../services/CommonFunctions';
import CategoryChips from '../../Components/CategoryChips';
import CustomRadioGroup from '../../Components/CustomRadioGroup';


import FormLabel from '@mui/joy/FormLabel';
import { getSignedUploadUrls } from '../../services/API/Api';
import { uploadFileToSignedUrl } from '../../services/API/Api';
import { ShortsCard } from '../Shorts/ShortsCards';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { validateForm } from './PostValidation';
import { AppDispatch } from '../../store';
import { fetchPlaces } from '../../services/CommonApiCall';
import { fetchCategories } from '../../services/CommonApiCall';
import { ITrip } from '../Trips/Trips';
import { draftUtils } from '../../Utils/draftStorage';
import { fileToBase64, base64ToFile } from '../../Utils/base64Converter';
import { ReactComponent as DraftIcon } from "../../Icons/svg/draft.svg"; 

// Extended interface to include type information
interface ITripWithType extends ITrip {
  type: 'Trip' | 'Itinerary';
}

interface PostFormState {
  title: string;
  description: string;
  tags: string[];
  categories: string[];
  placeRefId: string;
  tripType: string;
  // selectedTripId: string;
  linkTo: {
    id: string;
    module: string;
  } | null;
  location: {
    coordinates: [number, number] | null;
    name: string;
  };
  files: {
    fileKey: string;
    fileType: string;
    featured?: boolean;
  }[];
  author: {
    _id: string;
    username: string;
  };
}

interface ValidationErrors {
  images?: string;
  location?: string;
  place?: string;
  title?: string;
  description?: string;
}

interface PreviewImage {
  url: string;
  caption: string;
  featured: boolean;
  file?: File; // Add the file object as optional (or required based on your need)
}

const PostInput = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const emptyForm = {
    title: '',
    description: '',
    tags: [],
    categories: [],
    placeRefId: '',
    tripType: '',
    // selectedTripId: '',
    linkTo: null,
    location: {
      coordinates: null,
      name: '',
    },
    files: [],
    author: {
      _id: user?._id,
      username: user?.username,
    },
  }
  const location = useLocation();
  const editPostData = location.state;
  const draftData = location.state?.draft;
  const isEdit = location.state?.isEdit;
  const isPreview = location.state?.isPreview;
  const [preview, setPreview] = useState<boolean>(isPreview || false);
  const [imagesData, setImagesData] = useState<PreviewImage[]>([])
  const [videosData, setVideosData] = useState<any[]>([])
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [form, setForm] = useState<PostFormState>(emptyForm);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  interface Category {
    name: string;
    priority: number;
    featured: boolean;
  }
  const places = useSelector((state: RootState) => state.places.places);
  // const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [trips, setTrips] = useState<ITripWithType[]>([]);
  const [type, setType] = useState<string>('');
  const [selectedTripType, setSelectedTripType] = useState<'Trip' | 'Itinerary'>('Trip');
  const [selectedTrip, setSelectedTrip] = useState<ITripWithType | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<{ label: string; id: string; placeRefId: string } | null>(null);
  const [draftSelectedTrip, setDraftSelectedTrip] = useState<ITripWithType | null>(null);
  const videoRefs = useRef<HTMLDivElement[]>([]);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const categories = useSelector((state: RootState) => state.category.categories);

  // Helper function to format trip dates
  const formatTripDates = (trip: ITripWithType) => {
    if (!trip.batches || trip.batches.length === 0) return '';
    const batch = trip.batches[0];
    const fromDate = new Date(batch.from);
    const toDate = new Date(batch.to);
    
    const formatDate = (date: Date) => {
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    };
    
    if (fromDate.getMonth() === toDate.getMonth()) {
      return `${formatDate(fromDate)}-${toDate.getDate()}`;
    } else {
      return `${formatDate(fromDate)}-${formatDate(toDate)}`;
    }
  };

  // Helper function to get trip category color
  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Adventure': '#4CAF50',
      'Spiritual': '#9C27B0',
      'View point': '#2196F3',
      'Cultural': '#FF9800',
      'Nature': '#4CAF50',
    };
    return colors[category] || '#757575';
  };

  
  useEffect(() => {
    // fetch categories and places
    fetchCategories(dispatch,'post-places');
    fetchPlaces(dispatch);
  }, []);

  useEffect(() => {
    const fetchTripsAndItineraries = async () => {
      try {
        console.log('Fetching trips and itineraries...');
        
        // Fetch trips
        const tripsRes = await request<{ success: boolean; data: ITrip[], message: string }>('/trips/user/my-trips', {
          method: 'GET',
          authorization: true,
        });
        
        // Fetch itineraries
        const itinerariesRes = await request<{ success: boolean; data: ITrip[], message: string }>('/itineraries/user/my-itineraries', {
          method: 'GET',
          authorization: true,
        });
        
        console.log('Trips response:', tripsRes);
        console.log('Itineraries response:', itinerariesRes);
        
        // Combine trips and itineraries with type information
        const tripsWithType = [
          ...(tripsRes?.data || []).map(trip => ({ ...trip, type: 'Trip' as const })),
          ...(itinerariesRes?.data || []).map(itinerary => ({ ...itinerary, type: 'Itinerary' as const }))
        ];
        
        setTrips(tripsWithType);
        console.log('Combined trips and itineraries:', tripsWithType);
      } catch (err) {
        console.error('Error fetching trips and itineraries:', err);
        setTrips([]);
      }
    }
    fetchTripsAndItineraries();
  }, []);

  useEffect(() => {
    if (location.state) {
      // Handle editing existing post
      if (editPostData && !draftData) {
        setType('image');
        setForm({
          title: editPostData.title,
          description: editPostData.description,
          tags: [],
          categories: editPostData.categories,
          tripType: editPostData.tripType,
          // selectedTripId: editPostData.selectedTripId || '',
          linkTo: editPostData.linkTo || null,
          location: {
            coordinates: [editPostData.location.coordinates[0], editPostData.location.coordinates[1]],
            name: editPostData.location.name,
          },
          placeRefId: editPostData.placeRefId,
          files: editPostData.files,
          author: {
            _id: editPostData.author._id,
            username: editPostData.author.username,
          },
        });
        
        setSelectedCategories(editPostData.categories);
        setImagesData(editPostData.files.map((file: any) => ({ url: file?.fileURL, caption: file.caption, featured: file.featured, file: file.file })));
        
        // Set selected place if placeRefId exists
        if (editPostData.placeRefId) {
          const place = places.find(p => p._id === editPostData.placeRefId);
          if (place) {
            setSelectedPlace({
              label: place.name,
              id: place.name,
              placeRefId: place._id
            });
          }
        }
        
        if (editPostData.tripType) {
          setSelectedTripType(editPostData.tripType);
        }
        // if (editPostData.selectedTripId) {
        //   const trip = trips.find(t => t._id === editPostData.selectedTripId);
        //   if (trip) {
        //     setSelectedTrip(trip);
        //   }
        // }
      }
      // Handle editing draft
      else if (draftData && isEdit) {
        setType(draftData.type);
        setCurrentDraftId(draftData.id);
        setForm({
          title: draftData.title,
          description: draftData.description,
          tags: draftData.tags,
          categories: draftData.categories,
          tripType: draftData.tripType,
          // selectedTripId: draftData.selectedTripId,
          linkTo: draftData.linkTo,
          location: draftData.location,
          placeRefId: draftData.placeRefId,
          files: draftData.files,
          author: draftData.author,
        });
        handleChange('linkTo', draftData.linkTo);
        
        setSelectedCategories(draftData.categories);
        
        // Convert base64 back to File objects when loading
        const reconstructedImagesData = (draftData.imagesData || []).map((img: any) => {
          const newFile = img.fileBase64 ? base64ToFile(img.fileBase64, img.fileName, img.fileType) : null;
          return {
            url: newFile ? URL.createObjectURL(newFile) : img.url, // Create new blob URL for the reconstructed file
            caption: img.caption,
            featured: img.featured,
            file: newFile
          };
        });
        
        const reconstructedVideosData = (draftData.videosData || []).map((vid: any) => {
          const newFile = vid.fileBase64 ? base64ToFile(vid.fileBase64, vid.fileName, vid.fileType) : null;
          return {
            url: newFile ? URL.createObjectURL(newFile) : vid.url, // Create new blob URL for the reconstructed file
            file: newFile
          };
        });
        
        setImagesData(reconstructedImagesData);
        setVideosData(reconstructedVideosData);
        
        // Set selected place if placeRefId exists
        if (draftData.placeRefId) {
          const place = places.find(p => p._id === draftData.placeRefId);
          if (place) {
            setSelectedPlace({
              label: place.name,
              id: place.name,
              placeRefId: place._id
            });
          }
        }
        
        if (draftData.tripType) {
          setSelectedTripType(draftData.tripType);
        }
        
        // Set selected trip from linkTo data
        if (draftData.linkTo && draftData.linkTo.id) {
          const trip = trips.find(t => t._id === draftData.linkTo.id);
          if (trip) {
            setDraftSelectedTrip(trip);
            setSelectedTrip(trip);
          }
        }
      }
      // Handle previewing draft
      else if (draftData && isPreview) {
        setType(draftData.type);
        setForm({
          title: draftData.title,
          description: draftData.description,
          tags: draftData.tags,
          categories: draftData.categories,
          tripType: draftData.tripType,
          // selectedTripId: draftData.selectedTripId,
          linkTo: draftData.linkTo,
          location: draftData.location,
          placeRefId: draftData.placeRefId,
          files: draftData.files,
          author: draftData.author,
        });
        
        setSelectedCategories(draftData.categories);
        
        // Convert base64 back to File objects when loading for preview
        const reconstructedImagesData = (draftData.imagesData || []).map((img: any) => {
          const newFile = img.fileBase64 ? base64ToFile(img.fileBase64, img.fileName, img.fileType) : null;
          return {
            url: newFile ? URL.createObjectURL(newFile) : img.url, // Create new blob URL for the reconstructed file
            caption: img.caption,
            featured: img.featured,
            file: newFile
          };
        });
        
        const reconstructedVideosData = (draftData.videosData || []).map((vid: any) => {
          const newFile = vid.fileBase64 ? base64ToFile(vid.fileBase64, vid.fileName, vid.fileType) : null;
          return {
            url: newFile ? URL.createObjectURL(newFile) : vid.url, // Create new blob URL for the reconstructed file
            file: newFile
          };
        });
        
        setImagesData(reconstructedImagesData);
        console.log('99check Draft Images 2', reconstructedImagesData);
        setVideosData(reconstructedVideosData);
        
        // Set selected place if placeRefId exists
        if (draftData.placeRefId) {
          const place = places.find(p => p._id === draftData.placeRefId);
          if (place) {
            setSelectedPlace({
              label: place.name,
              id: place.name,
              placeRefId: place._id
            });
          }
        }
        
        if (draftData.tripType) {
          setSelectedTripType(draftData.tripType);
        }
        
        // Set selected trip from linkTo data
        if (draftData.linkTo && draftData.linkTo.id) {
          const trip = trips.find(t => t._id === draftData.linkTo.id);
          if (trip) {
            setDraftSelectedTrip(trip);
            setSelectedTrip(trip);
          }
        }
      }
    }
  }, [editPostData, draftData, isEdit, isPreview, trips, places]);

  const handleDescriptionBlur = () => {
    const hashtags = extractHashtags(form.description);
    if (hashtags.length > 0) {
      setForm(prev => ({
        ...prev,
        tags: Array.from(new Set([...prev.tags, ...hashtags]))
      }));
    }
  };

  const handleChange = <K extends keyof PostFormState>(field: K, value: PostFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Validation function similar to createTrip
  const validatePostForm = (): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    // Validate images/videos
    const mediaFiles = type === 'image' ? imagesData : videosData;
    if (!mediaFiles || mediaFiles.length === 0) {
      errors.images = 'Please upload at least one ' + (type === 'image' ? 'image' : 'video');
      isValid = false;
    }

    // Validate title
    if (!form.title || !form.title.trim()) {
      errors.title = 'Title is required';
      isValid = false;
    }

    // Validate description
    if (!form.description || !form.description.trim()) {
      errors.description = 'Description is required';
      isValid = false;
    }

    // Validate location
    if (!form.location.coordinates || !form.location.name) {
      errors.location = 'Location is required';
      isValid = false;
    }

    // Validate place
    if (!form.placeRefId || form.placeRefId === '') {
      errors.place = 'Please select a place';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  // Helper to submit post and handle navigation/alerts
  const submitPost = async (body: PostFormState) => {
    try {
      const response = await request<any, PostFormState>('/posts', {
        method: 'POST',
        body,
        authorization: true,
      });
      console.log('Post submitted:', response);
      alert(editPostData ? 'Post updated successfully' : 'Post created successfully');
      
      /**
       * STACK CLEAR REQUIRED HERE:
       * After successful post submission, we replace the input form page in history to:
       * 1. Prevent users from going back to the empty/filled form after successful submission
       * 2. Avoid accidental duplicate post submissions if user navigates back
       * 3. Provide clean navigation flow to the posts feed/shorts page
       * 4. Clear potentially large media/file data from browser history
       * 5. Match standard UX patterns for content creation workflows
       */
      navigate(type === 'image' ? '/post' : '/shorts', { replace: true });
    } catch (error) {
      console.error('Error during submission:', error);
      alert(`Post submission failed: ${error}`);
    }
  };

  const updatePost = async (body: PostFormState) => {
    try {
      const response = await request<any, PostFormState>(`/posts/${editPostData._id}`, {
        method: 'PUT',
        body,
        authorization: true,
      });
      console.log('Post updated:', response);
      alert('Post updated successfully');
      
      /**
       * STACK CLEAR REQUIRED HERE:
       * After successful post update, we replace the edit form page in history to:
       * 1. Prevent users from returning to the edit form after successful update
       * 2. Avoid confusion or accidental re-editing of the same content
       * 3. Provide clean transition back to the posts feed/shorts page
       * 4. Remove edit form state from browser history
       * 5. Follow consistent UX patterns with new post creation flow
       */
      navigate(type === 'image' ? '/post' : '/shorts', { replace: true });
    } catch (error) {
      console.error('Error during submission:', error);
      alert(`Post submission failed: ${error}`);
    }
  }

  const handleSubmit = async () => {
    // Mark submit as attempted to show all validation errors
    setSubmitAttempted(true);
    
    // Validate the form
    if (!validatePostForm()) {
      // Scroll to top to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    const isImage = type === 'image';
    const mediaFiles = isImage ? imagesData : videosData;
    if (!mediaFiles || mediaFiles.length === 0) {
      console.error('No media files selected');
      return;
    }
    try {
      // Step 1: Prepare filesProp for all media
      const filesProp = mediaFiles.map((item: any) => ({
        folder: 'post',
        fileType: String(item.file.type),
      }));
      const signedUrlsData = await getSignedUploadUrls(filesProp);
      const signedFiles = signedUrlsData?.data?.files || [];
      if (signedFiles.length !== mediaFiles.length) {
        console.error('Mismatch in signed URLs and media files count');
        return;
      }
      const uploadedFiles = await Promise.all(mediaFiles.map(async (item: any, i: number) => {
        const { uploadUrl, fileKey } = signedFiles[i];
        if (!uploadUrl) return null;
        const uploadResponse = await uploadFileToSignedUrl(item.file, uploadUrl);
        if (uploadResponse.status === 200) {
          return {
            fileKey,
            fileType: String(item.file.type),
            featured: i === 0,
          };
        }
        return null;
      }));
      const validFiles: { fileKey: string; fileType: string; featured: boolean }[] = uploadedFiles.filter((f): f is { fileKey: string; fileType: string; featured: boolean } => f !== null);
      if (validFiles.length === 0) {
        console.error('No files were uploaded successfully');
        return;
      }
      await submitPost({ ...form, files: validFiles });
    } catch (error) {
      console.error('Error during submission:', error);
      alert(`Post creation failed: ${error}`);
    }
  };

  const handlePreviewToggle = (previewState: boolean) => {
    if (previewState) {
      // Mark submit as attempted to show all validation errors
      setSubmitAttempted(true);
      
      // Validate before showing preview
      if (!validatePostForm()) {
        // Scroll to top to show errors
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    setPreview(previewState);
    setType(preview => (preview));
    // Ensure form data is preserved by explicitly setting it again
    setForm(prevForm => ({ ...prevForm }));
  };


  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      const isImage = type === 'image';
      const mediaFiles = isImage ? imagesData : videosData;
      
      // Convert File objects to base64 when saving
      const serializedImagesData = await Promise.all(imagesData.map(async img => ({
        url: img.url,
        caption: img.caption,
        featured: img.featured,
        fileBase64: img.file ? await fileToBase64(img.file) : null,
        fileName: img.file?.name || '',
        fileType: img.file?.type || '',
        fileSize: img.file?.size || 0
      })));

      const serializedVideosData = await Promise.all(videosData.map(async vid => ({
        url: vid.url,
        fileBase64: vid.file ? await fileToBase64(vid.file) : null,
        fileName: vid.file?.name || '',
        fileType: vid.file?.type || '',
        fileSize: vid.file?.size || 0
      })));

      const draftData = {
        title: form.title,
        description: form.description,
        tags: form.tags,
        categories: form.categories,
        placeRefId: form.placeRefId,
        tripType: form.tripType,
        // selectedTripId: form.selectedTripId,
        linkTo: form.linkTo,
        location: form.location,
        files: form.files,
        author: form.author,
        imagesData: serializedImagesData,
        videosData: serializedVideosData,
        type: type || 'image',
      };

      if (currentDraftId) {
        draftUtils.updateDraft(dispatch, currentDraftId, draftData);
        alert('Draft updated successfully!');
      } else {
        draftUtils.saveDraft(dispatch, draftData);
        setCurrentDraftId(Date.now().toString()); // Use timestamp as ID
        alert('Draft saved successfully!');
        console.log('99check1 Draft Data save', draftData);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <Box p={2} sx={{ alignItems: 'center', backgroundColor: '#ffffff',mb: 20 }} >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">
          New Post
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!preview && !isPreview && (
            <>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}   onClick={() => navigate('/post/drafts', { state: { filterType: 'post' } })}>
                <DraftIcon />
                <Typography fontWeight={"medium"} sx={{ color: '#008387', textTransform: 'none' }}>{'Open from Draft'}</Typography>
                </Box>
            </>
          )}
        </Box>
      </Box>

      {!preview ? <Stack spacing={3}>
        {/* Media Upload Section */}
        <Box>
          {/* <Typography variant="h6">{JSON.stringify(imagesData,null,2)}</Typography> */}
          <MediaUpload
            value={type == 'image' ? imagesData : videosData}
            onImagesChange={(images: any) => {
              console.log('99 Normal Images', images);
              setImagesData(images);
              // Clear image validation error when images are added
              if (images.length > 0) {
                setValidationErrors(prev => ({ ...prev, images: undefined }));
              }
            }}
            onVideoChange={(videos: any) => {
              console.log('videos outside', videos);
              setVideosData(videos);
              // Clear video validation error when videos are added
              if (videos.length > 0) {
                setValidationErrors(prev => ({ ...prev, images: undefined }));
              }
            }}
            maxImages={5}
            setType={setType}
            type={type || 'image'}
          />
          {submitAttempted && validationErrors.images && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
              {validationErrors.images}
            </Typography>
          )}
        </Box>

        {/* Basic Information */}
        <TextField
          fullWidth
          label="Title *"
          value={form.title}
          onChange={(e) => {
            handleChange('title', e.target.value);
            // Clear title error when user types
            if (e.target.value.trim()) {
              setValidationErrors(prev => ({ ...prev, title: undefined }));
            }
          }}
          required
          error={submitAttempted && !!validationErrors.title}
          helperText={submitAttempted && validationErrors.title ? validationErrors.title : ''}
        />

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Description *"
          value={form.description}
          onChange={(e) => {
            handleChange('description', e.target.value);
            // Clear description error when user types
            if (e.target.value.trim()) {
              setValidationErrors(prev => ({ ...prev, description: undefined }));
            }
          }}
          onBlur={handleDescriptionBlur}
          required
          error={submitAttempted && !!validationErrors.description}
          helperText={submitAttempted && validationErrors.description ? validationErrors.description : ''}
        />

     
              <CategoryChips
                categories={categories}
                selectedCategories={selectedCategories}
                onChange={(newCategories) => {
                  setSelectedCategories(newCategories.map(category => category.name));
                  setForm(prev => ({
                    ...prev,
                    categories: newCategories.map(category => category.name)
                  }));
                }}
                maxInitialDisplay={6}
              />

        {/* Location */}
        <Box>
          <AutoCompletePlaces
            key={form.location.coordinates?.toString()}
            value={{
              name: form.location.name,
              lat: form.location.coordinates ? form.location.coordinates[0] : 0,
              lng: form.location.coordinates ? form.location.coordinates[1] : 0,
            }}
            onPlaceSelect={(place) => {
              handleChange('location', {
                coordinates: [place.lng, place.lat],
                name: place.name,
              });
              // Clear location error when location is selected
              setValidationErrors(prev => ({ ...prev, location: undefined }));
            }}
            label="Location *"
            placeholder="Enter a location"
          />
          {submitAttempted && validationErrors.location && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
              {validationErrors.location}
            </Typography>
          )}
        </Box>
        <Box>
          <Autocomplete
            disablePortal
            options={places.map((place: any) => ({ label: place.name, id: place.name, placeRefId: place._id }))}
            value={selectedPlace}
            sx={{ width: '100%'}}
            renderInput={(params) => <TextField 
              {...params} 
              label="Places *" 
              error={submitAttempted && !!validationErrors.place}
            />}
            onChange={(event, value) => {
              setSelectedPlace(value);
              handleChange('placeRefId', value?.placeRefId || '');
              // Clear place error when place is selected
              if (value) {
                setValidationErrors(prev => ({ ...prev, place: undefined }));
              }
            }}
          />
          {submitAttempted && validationErrors.place && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
              {validationErrors.place}
            </Typography>
          )}
        </Box>


        <FormControl>
          <FormLabel sx={{ fontSize: '16px', fontWeight: '500', mb: 2 }}>Link</FormLabel>
          
          {/* Trip Type Toggle Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant={selectedTripType === 'Trip' ? 'contained' : 'outlined'}
              onClick={() => {
                setSelectedTripType('Trip');
                // Clear selection when switching types
                setSelectedTrip(null);
                setDraftSelectedTrip(null); // Clear draft state when switching types
                handleChange('tripType', 'Trip');
                handleChange('linkTo', null);
              }}
              sx={{
                borderRadius: '20px',
                backgroundColor: selectedTripType === 'Trip' ? '#F57C00' : 'transparent',
                color: selectedTripType === 'Trip' ? 'white' : '#F57C00',
                borderColor: '#F57C00',
                '&:hover': {
                  backgroundColor: selectedTripType === 'Trip' ? '#E65100' : '#FFF3E0',
                },
                textTransform: 'none',
                fontWeight: '500',
              }}
            >
              Link trips
            </Button>
            <Button
              variant={selectedTripType === 'Itinerary' ? 'contained' : 'outlined'}
              onClick={() => {
                setSelectedTripType('Itinerary');
                // Clear selection when switching types
                setSelectedTrip(null);
                setDraftSelectedTrip(null); // Clear draft state when switching types
                handleChange('tripType', 'Itinerary');
                handleChange('linkTo', null);
              }}
              sx={{
                borderRadius: '20px',
                backgroundColor: selectedTripType === 'Itinerary' ? '#F57C00' : 'transparent',
                color: selectedTripType === 'Itinerary' ? 'white' : '#F57C00',
                borderColor: '#F57C00',
                '&:hover': {
                  backgroundColor: selectedTripType === 'Itinerary' ? '#E65100' : '#FFF3E0',
                },
                textTransform: 'none',
                fontWeight: '500',
              }}
            >
              Link Itinerary
            </Button>
          </Box>

          {/* Trip Selection Cards */}
          {trips.length > 0 && (
            <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
              {(() => {
                const filteredTrips = trips.filter(trip => trip.type === selectedTripType);
                return filteredTrips.map((trip, index) => (
                <Box key={trip._id}>
                  <Card
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      border: selectedTrip?._id === trip._id ? '2px solid #F57C00' : '1px solid #E0E0E0',
                      borderRadius: '8px',
                      '&:hover': {
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => {
                      setSelectedTrip(trip);
                      setDraftSelectedTrip(null); // Clear draft state when user selects new trip
                      const linkToObject = {
                        id: trip._id,
                        module: selectedTripType
                      };
                      console.log('Creating linkTo object:', linkToObject);
                      handleChange('tripType', selectedTripType);
                      handleChange('linkTo', linkToObject);
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Trip Image */}
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: '#F5F5F5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {trip.files && trip.files.length > 0 ? (
                            <CardMedia
                              component="img"
                              image={trip.files[0].fileURL}
                              alt={trip.title}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No Image
                            </Typography>
                          )}
                        </Box>

                        {/* Trip Details */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                            {trip.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                              label={trip.category[0] || 'General'}
                              size="small"
                              sx={{
                                backgroundColor: getCategoryColor(trip.category[0] || 'General'),
                                color: 'white',
                                fontSize: '0.75rem',
                                height: '20px',
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              ₹{trip.pricePerPerson}/person
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatTripDates(trip)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                  {index < filteredTrips.length - 1 && <Divider sx={{ my: 1 }} />}
                </Box>
                ));
              })()}
            </Box>
          )}

          {trips.filter(trip => trip.type === selectedTripType).length === 0 && trips.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No {selectedTripType.toLowerCase()}s available. Create a {selectedTripType.toLowerCase()} first to link it to your post.
            </Typography>
          )}

          {trips.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No trips or itineraries available. Create one first to link it to your post.
            </Typography>
          )}

         

          {/* Selected Trip Indicator */}
          {selectedTrip && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#FFF3E0', borderRadius: '8px', border: '1px solid #F57C00' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="#F57C00" fontWeight="bold">
                    Selected: {selectedTrip.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedTripType} • ₹{selectedTrip.pricePerPerson}/person • {formatTripDates(selectedTrip)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedTrip(null);
                    setDraftSelectedTrip(null); // Clear draft state when clearing selection
                    handleChange('linkTo', null);
                  }}
                  sx={{ 
                    color: '#F57C00', 
                    minWidth: 'auto',
                    fontSize: '0.75rem'
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          )}
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
              variant="outlined"
              onClick={handleSaveDraft}
              sx={{
                borderColor: '#FF6B35',
                color: '#FF6B35',
                py: 1.5,
                px: 4,
                borderRadius: '25px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              {'Save as Draft'}
            </Button>
          <Button variant="outlined" size="large" sx={{ borderRadius: '20px', color: '#F57C00', borderColor: '#F57C00' }} onClick={() => handlePreviewToggle(true)}>
            Preview
          </Button>
        </Box>

      </Stack> :
        <Box sx={{ width: '95%' }}>

          {/*  check the form when preview is on then show the form data */}
          {type === 'image' ? (
            <>
              {console.log('Preview imagesData:', imagesData)}
              {console.log('Preview files mapping:', imagesData.map((img: any) => ({ 
                fileURL: img.url,
                caption: img.caption,
                featured: img.featured 
              })))}
              <PostUI
                item={{
                  author: { username: form.author.username },
                  title: form.title,
                  description: form.description,
                  files: imagesData.map((img: any) => ({ 
                    fileURL: img.url,
                    caption: img.caption,
                    featured: img.featured 
                  })),
                  location: { name: form.location.name },
                  createdAt: '',
                  likes: [],
                  shop: false,
                  place: { name: form.placeRefId },
                  id: '',
                }}
                isPreview={true}
              />
            </>

          ) : (
            <ShortsCard
              item={{
                author: { username: form.author.username },
                description: form.description,
                files: videosData.map((vid: any) => ({ 
                  fileURL: vid.url 
                })),
                likes: [],
                location: form.location,
              }}
              index={0}
              activeIndex={0}
              handleShare={() => { }}
              videoRefs={videoRefs}
              isPreview={true}
              handleFollow={() => { }}
              currentUser={user}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: 1, marginBottom: 20 }}>
            <Button variant="outlined" size="large" sx={{ mt: 2, color: '#49454F', borderRadius: '100px', borderColor: '#CAC4D0' }} onClick={() => handlePreviewToggle(false)}>
              Edit
            </Button>
              <Button variant="contained" color="primary" size="large" sx={{ mt: 2, borderRadius: '100px', backgroundColor: '#F57C00', color: '#fffff' }} onClick={() => {
                handleSubmit()
              }}>
                 Post
              </Button>
          </Box>
        </Box>}
    </Box>
  );
};

export default PostInput;
